<template>
  <ButtonTransition :buttonId="buttonId">
    <LinkWrapper
      :href="href"
      :class="[classes.oddsButton, testClassName, 'button', $attrs.class]"
      target="_blank"
      :disabled="disabled"
      :title="title"
    >
      <StyledText
        v-if="name"
        tag="span"
        :fontId="buttonStyle.secondaryFont"
        :class="[classes.name, 'name']"
      >
        {{ name }}
      </StyledText>

      <div v-if="suspended" :style="{ flex: !name ? 1 : '' }">
        <Lock :color="lockIconColor" width="15" height="15" />
      </div>

      <div v-else-if="disabled">
        <StyledText
          tag="span"
          :fontId="buttonStyle?.primaryFont"
          :class="[classes.oddsText, 'odds-text', !!meta && classes.small]"
        >
          -
        </StyledText>
      </div>

      <div v-else :class="classes.oddsWrapper">
        <StyledText
          v-if="meta"
          tag="span"
          :fontId="buttonStyle.secondaryFont"
          :class="[classes.metaText, 'meta-text']"
        >
          {{ meta }}
        </StyledText>
        <StyledText
          v-if="showWinnings"
          tag="span"
          :fontId="buttonStyle?.primaryFont"
          :class="[classes.oddsText, 'odds-text', !!meta && classes.small]"
        >
          {{ winnings }}
        </StyledText>
        <StyledText
          v-else
          tag="span"
          :fontId="buttonStyle.primaryFont"
          :class="[classes.oddsText, 'odds-text', !!meta && classes.small]"
        >
          {{ odds }}
        </StyledText>
      </div>
    </LinkWrapper>
  </ButtonTransition>
</template>

<script lang="ts">
import { defineComponent, computed, inject } from "vue";
import { createUseStyles } from "@fresh8/ad-data-composition/src/theme";
import LinkWrapper from "../LinkWrapper.vue";
import {
  BrandContext,
  ButtonStyle,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import StyledText from "./Text.vue";
import Lock from "../icons/Lock.vue";
import { useShowWinnings } from "@fresh8/ad-data-composition/src/usePotentialWinnings";
import ButtonTransition from "../ButtonTransition.vue";

export default defineComponent({
  name: "OddsButton",
  components: {
    LinkWrapper,
    StyledText,
    Lock,
    ButtonTransition
  },
  props: {
    href: {
      type: String,
      required: false,
      default: ""
    },
    meta: {
      type: String,
      required: false,
      default: ""
    },
    name: {
      type: String,
      required: false,
      default: ""
    },
    odds: {
      type: String,
      required: false,
      default: ""
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    buttonId: {
      type: String,
      required: false,
      default: ""
    },
    suspended: {
      type: Boolean,
      required: false,
      default: false
    },
    winnings: {
      type: String,
      required: false,
      default: "0"
    }
  },
  setup(props) {
    const stagger = inject("stagger", 0);
    const { getButtonStyle, getFontStyle } =
      injectBrand() || ({} as BrandContext);
    const buttonStyle = computed(() => {
      return getButtonStyle(props.buttonId);
    });
    const buttonEnabled = computed(() => !props.disabled);
    const styleComputed = computed(() => ({
      buttonStyle: buttonStyle.value,
      buttonEnabled: buttonEnabled.value,
      primaryColor: getFontStyle(buttonStyle.value?.primaryFont)?.color,
      secondaryColor: getFontStyle(buttonStyle.value?.secondaryFont)?.color
    }));

    const showWinnings = useShowWinnings();

    const lockIconColor = computed(
      () => getFontStyle(buttonStyle.value?.primaryFont)?.color || "#000"
    );

    const title = computed(() => {
      if (props.disabled) {
        return "-";
      }

      if (props.suspended) {
        return `${props.name} - Suspended`;
      }

      return `${props.name} - ${props.odds}`;
    });

    const { classesRef } = useStyles(styleComputed);

    return {
      stagger,
      title,
      classes: classesRef,
      buttonStyle,
      lockIconColor,
      buttonEnabled,
      testClassName: computed(() => "text-style-market-name"),
      showWinnings
    };
  }
});

const defaultStyling = {
  backgroundColor: "#fff",
  borderRadius: "",
  borderColor: "",
  borderStyle: "",
  borderWidth: "",
  autoInteractions: true
};

const { useStyles } = createUseStyles<
  "oddsButton" | "metaText" | "oddsText" | "small" | "name" | "oddsWrapper",
  {
    buttonStyle: ButtonStyle;
    buttonEnabled: boolean;
    primaryColor: string;
    secondaryColor: string;
  }
>({
  oddsButton: {
    backgroundColor: props =>
      props.buttonStyle.backgroundColor || defaultStyling.backgroundColor,
    borderRadius: props => {
      if (props.buttonStyle.borderRadius === 0) {
        return 0;
      }

      return props.buttonStyle.borderRadius || defaultStyling.borderRadius;
    },
    borderColor: props =>
      props.buttonStyle.borderColor || defaultStyling.borderColor,
    borderStyle: props =>
      props.buttonStyle.borderStyle || defaultStyling.borderStyle,
    borderWidth: props =>
      props.buttonStyle.borderWidth || defaultStyling.borderWidth,
    "&:hover": {
      backgroundColor: props => props.buttonStyle.actions.hover.backgroundColor
    },
    "&:active": {
      backgroundColor: props => props.buttonStyle.actions.active.backgroundColor
    },
    "& > div": {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative"
    },
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxSizing: "border-box",
    padding: "4px 8px"
  },
  metaText: {
    fontSize: "10px",
    lineHeight: 1.2
  },
  oddsText: {
    fontSize: "14px",
    lineHeight: 1,
    color: props => props.primaryColor || "#000",
    "$oddsButton:hover &": {
      color: props => props.buttonStyle.actions.hover.primaryColor
    },
    "$oddsButton:active &": {
      color: props => props.buttonStyle.actions.active.primaryColor
    }
  },
  small: {
    fontSize: "12px"
  },
  name: {
    fontSize: "14px",
    color: props => props.secondaryColor || "#000",
    "$oddsButton:hover &": {
      color: props => props.buttonStyle.actions.hover.secondaryColor
    },
    "$oddsButton:active &": {
      color: props => props.buttonStyle.actions.active.secondaryColor
    }
  },
  oddsWrapper: {
    // marginLeft: "8px",
    color: props => props.secondaryColor || "#000",
    "$oddsButton:hover &": {
      color: props => props.buttonStyle.actions.hover.secondaryColor
    },
    "$oddsButton:active &": {
      color: props => props.buttonStyle.actions.active.secondaryColor
    }
  }
});
</script>
